<template>
    <div class="gift-main-container">
        <div class="inputs">
            <div class="start-input">
                <ModalField class="flex-1 w-full" label="Recipient Name" input-id="giftcert-recipient-name">
                    <prime-input-text id="giftcert-recipient-name" v-model="name" class="name-input" placeholder="John Doe" @keydown.enter="onEnter" @keydown="letterOnlyInputHandler" />
                </ModalField>
                <ModalField
                    class="flex-1 w-full"
                    label="Gift Certificate Amount"
                    input-id="giftcert-amount"
                    :error="amountError"
                    v-slot="{ errorId, invalid }"
                >
                    <!-- The box is the field: the text field inside it is borderless, so
                         the ring it gives up (`data-focus-ring`) is drawn round the box by
                         the design system's `pb-field-shell`, and the dollar sign sits inside it. -->
                    <div class="amount-container pb-field-shell" :class="[showAmountError ? 'error' : '', 'border-zinc-300']">
                        <div class="amount-error" :class="showAmountError ? 'border-r-red-300' : 'border-r-zinc-300'">
                            <p aria-hidden="true">$</p>
                        </div>
                        <prime-input-text
                            id="giftcert-amount"
                            v-model="amountInput"
                            data-focus-ring="none"
                            class="amount-input outline-none"
                            placeholder="50.00"
                            :aria-describedby="invalid ? errorId : 'giftcert-amount-info'"
                            :aria-invalid="invalid"
                            @input="validateNumericInput"
                            @keydown="preventNonNumericKeys"
                            @keydown.enter="onEnter"
                        />
                    </div>
                    <p v-if="!invalid" id="giftcert-amount-info" class="amount-info">Minimum amount: $20.00</p>
                </ModalField>
            </div>
            <ModalField
                class="w-full"
                label="Recipient Email"
                input-id="giftcert-recipient-email"
                :error="isEmailInvalid ? 'Please enter a valid email address.' : undefined"
                v-slot="{ errorId, invalid }"
            >
                <prime-input-text
                    id="giftcert-recipient-email"
                    v-model="email"
                    class="email-input"
                    placeholder="johndoe@email.com"
                    :aria-describedby="errorId"
                    @keydown.enter="onEnter"
                    :invalid="invalid"
                />
            </ModalField>
        </div>
        <div class="summary-section">
            <div class="summary-header">
                <p>Summary</p>
            </div>
        </div>
        <div class="summary-footer">
            <span>Due Today</span>
            <p>{{ formattedAmount }}</p>
        </div>
        <footer-gift-cert
            :isFormValid="isFormValid"
            :isLoading="isLoading"
            @purchase="handlePurchase"
            @update:isChecked="isChecked = $event"
        />
    </div>
</template>
<script setup lang="ts">
import { formatCurrency } from '@privacy-bee/format/currency';
import { isValidEmail } from '@privacy-bee/validate/email';
import { useLicenseGiftingStore } from '~/stores/person/license-gifting';
import PrimeInputText from 'primevue/inputtext';
import { onBeforeUnmount, ref } from 'vue';
import { getDialogRef, useDynamicDialog } from '~/composables/dynamic-dialog';
import { useAuthStore } from '~/stores/auth';
import { useBillingStore } from '~/stores/person/billing';
import { ModalNames } from '~/types/modals';
import type { GiftCertificatePurchaseResponse } from '~/types/person/financial';
import { ApiError } from '~/utils/errors';
import ModalField from '~/components/modals/shared/ModalField.vue';
import FooterGiftCert from './FooterGiftCert.vue';

/** app-api's reason for a purchase with no usable card to charge (`PaymentRefusedException`). */
const NO_PAYMENT_METHOD = 'no_payment_method';

const { letterOnlyInputHandler } = useUtils();
const store = useLicenseGiftingStore();
const billingStore = useBillingStore();
const authStore = useAuthStore();
const nuxtApp = useNuxtApp();
const dialogRef = getDialogRef(ModalNames.GIFTCERT);

const name = ref('');
const email = ref('');
const amountInput = ref('');
const isChecked = ref(false);
const isLoading = ref(false);

const amount = computed(() => {
    const parsed = parseFloat(amountInput.value);
    return isNaN(parsed) ? 0 : parsed;
});

const formattedAmount = computed(() => {
    return formatCurrency(amount.value * 100);
});

const isEmailInvalid = computed(() => email.value.trim() !== '' && !isValidEmail(email.value.trim()));

const isFormValid = computed(() => {
    const valid =
        name.value.trim() !== '' && email.value.trim() !== '' && amount.value >= 20 && amount.value <= 500 && isChecked.value && isValidEmail(email.value.trim());

    return valid;
});

const showAmountError = computed(() => {
    return amount.value > 0 && amount.value < 20;
});

const showAmountTooHighError = computed(() => {
    return amount.value > 500;
});

const amountError = computed(() => {
    if (showAmountError.value) return 'Amount must be at least $20.00';
    if (showAmountTooHighError.value) return 'Amount cannot exceed $500.00';
    return undefined;
});

const validateNumericInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
        const filteredValue = parts[0] + '.' + parts.slice(1).join('');
        target.value = filteredValue;
        amountInput.value = filteredValue;
    } else {
        // Limit decimal places to maximum of 2
        if (parts.length === 2 && parts[1].length > 2) {
            const filteredValue = parts[0] + '.' + parts[1].substring(0, 2);
            target.value = filteredValue;
            amountInput.value = filteredValue;
        } else {
            target.value = numericValue;
            amountInput.value = numericValue;
        }
    }
};

const preventNonNumericKeys = (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const cursorPosition = target.selectionStart || 0;

    // Allow: backspace, delete, tab, escape, enter, arrow keys
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].includes(event.keyCode)) {
        return;
    }

    // Allow: numbers 0-9
    if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
        // Check if adding this number would exceed 2 decimal places
        if (value.includes('.')) {
            const decimalIndex = value.indexOf('.');
            const decimalPlaces = cursorPosition - decimalIndex - 1;
            if (decimalPlaces >= 2) {
                event.preventDefault();
                return;
            }
        }
        return;
    }

    // Allow: decimal point (but only one)
    if (event.keyCode === 190 || event.keyCode === 110) {
        if (value.includes('.')) {
            event.preventDefault();
            return;
        }
        return;
    }

    // Prevent all other keys
    event.preventDefault();
};

const handlePurchase = async () => {
    isLoading.value = true;
    try {
        // Check if user has a payment method
        if (!authStore.user?.identity?.payment_method?.type) {
            // No payment method - show payment modal
            handleShowPaymentModal();
            return;
        }

        // Has payment method - proceed with purchase
        showPurchased(await billingStore.purchaseGiftCertificate(name.value, email.value, amount.value));
    } catch (error: unknown) {
        // A refusal is already on screen from the billing store, and the purchased modal stays
        // shut. Anything else is a fault here, and is left to fail visibly.
        if (!(error instanceof ApiError)) throw error;
        // The API found no card to charge: the card form lets the purchase go ahead with a new one.
        if (error.reason === NO_PAYMENT_METHOD) handleShowPaymentModal();
    } finally {
        isLoading.value = false;
    }
};

/**
 * Hands the bought code to the confirmation. The code is unrecoverable once that dialog is
 * dismissed without emailing or copying it; `useDynamicDialog`'s defaults keep Escape and a click
 * on the mask from closing it, so its leave-confirmation guard is the only way out.
 */
function showPurchased(result: GiftCertificatePurchaseResponse) {
    store.setGiftCertificateDetails(result.data.code, email.value, name.value, result.data.amount);
    dialogRef?.value?.close();
    useDynamicDialog(ModalNames.GIFTCERT_PURCHASED, undefined, { label: 'Gift certificate purchased' });
}

/**
 * Stops listening for the card form's success. One listener at a time, and none once this modal is
 * gone: `payment_method:success` is an app-wide hook, so a listener left behind by a card form the
 * buyer cancelled would buy its certificate alongside the next one, on the next card anyone saves.
 */
let stopListening: (() => void) | null = null;

onBeforeUnmount(() => stopListening?.());

const handleShowPaymentModal = () => {
    // Reset loading state since we're showing the payment modal
    isLoading.value = false;

    stopListening?.();
    stopListening = nuxtApp.hook('payment_method:success', async (payload: { paymentMethodId: string }) => {
        // One purchase per card form: unhooked before the charge, so a second success event can
        // never buy a second certificate.
        stopListening?.();
        stopListening = null;
        try {
            // Proceed with gift certificate purchase using the new payment method
            showPurchased(await billingStore.purchaseGiftCertificate(
                name.value,
                email.value,
                amount.value,
                payload.paymentMethodId
            ));
        } catch (error: unknown) {
            // A refusal is already on screen from the billing store; anything else fails visibly.
            if (!(error instanceof ApiError)) throw error;
        }
    });

    useDynamicDialog(ModalNames.STRIPE_PAYMENT);
};

// Awaited, so a fault in the purchase reaches Vue's error handler as the button's does.
const onEnter = async () => {
    if (isFormValid.value && !isLoading.value) {
        await handlePurchase();
    }
};
</script>
<style scoped lang="scss">
.gift-main-container {
    @apply flex items-start self-stretch flex-col w-full;

    .inputs {
        @apply flex flex-col items-start gap-6 w-full;

        .start-input {
            @apply flex flex-col lg:flex-row items-start gap-6 w-full;

            .amount-container {
                @apply flex items-start self-stretch rounded-sm border;

                &.error {
                    @apply border-red-300;
                }

                .amount-error {
                    @apply flex p-2 px-3 items-center border-r;

                    p {
                        @apply text-gray-600 text-base font-normal leading-6;
                    }
                }
            }
        }
    }
}
.name-input {
    @apply flex p-2.5 px-3 items-center w-full gap-2 self-stretch rounded-sm border border-zinc-300 bg-white;
}
.amount-input {
    @apply w-full flex p-2.5 px-3 w-full items-center gap-2 self-stretch rounded-sm border !border-white bg-white;
}
.email-input {
    @apply w-full p-2 px-3 items-center gap-2 rounded-sm bg-white;
}
.amount-info {
    // mt-0.5 under the field's gap-1.5 is the same 8px the validation message takes.
    @apply text-zinc-500 text-xs font-normal leading-4 mt-0.5;
}
.summary-section {
    @apply flex py-6 items-center gap-1 self-stretch;

    .summary-header {
        @apply flex items-center gap-2 flex-1;

        p {
            @apply text-zinc-900 text-lg font-semibold leading-8;
        }
    }
}
.summary-footer {
    @apply flex justify-between items-center self-stretch py-3 border-b border-t border-dashed border-zinc-300;

    span {
        @apply text-zinc-800 text-base font-semibold leading-8;
    }
    p {
        @apply text-zinc-950 text-right text-base font-medium leading-8;
    }
}
</style>
